import { defaultErrorHandler, getReadFriendlyErrorMessage } from "./errors";

/**
 * @typedef {Object} QuestionWithFeedback
 * @property {number} attemptId
 * @property {number} attemptUserUid
 * @property {string} attemptUserDisplayName
 * @property {number} questionNumber
 * @property {boolean} hasUnreadMessages
 * @property {string} latestMessage
 * @property {number} latestMessageTimestamp
 * @property {boolean} latestMessageSenderUid
 * @property {string} latestMessageSenderDisplayName
 * @property {number} taskId
 * @property {string} taskLabel
 * @property {number} taskSetTimestamp
 * @property {string} taskType
 */

/**
 * Get all question numbers of task attempts that contains
 * recent feedback relevant to the logged-in user.
 *
 * The API method only considers feedback within a fixed time
 * interval (currently set to 1 month).
 *
 * @param {Object} options
 * @param {number} options.limit
 * @param {number} options.offset
 * @param {string} options.status
 * @returns {Promise<{ totalResults: number, results: QuestionWithFeedback[]}>}
 */
export async function getLatestFeedbackByQuestion({ limit, offset, status }) {
  try {
    const response = await fetch(
      `/api/tasks/feedback/latest?limit=${limit}&offset=${offset}&status=${status}`,
    );
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.message);
    }
    const data = await response.json();
    return data;
  } catch (e) {
    console.log(e.message);
    const readFriendlyErrorMessage =
      getReadFriendlyErrorMessage(e.message) ??
      "There was an error retrieving feedback.";
    defaultErrorHandler(readFriendlyErrorMessage);
  }
}

/**
 * Gets a user answer object for a specific question on a specific
 * assessment attempt. This includes any associated feedback.
 * Also gets the question object itself.
 *
 * @returns {Promise<{ question: Object, useranswer: Object, user: Object }>}
 */
export async function getUserAnswer(attemptId, questionNumber) {
  try {
    const response = await fetch(
      `/api/tasks/useranswer/${attemptId}/${questionNumber}`,
    );
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.message);
    }
    const data = await response.json();
    return data;
  } catch (e) {
    console.log(e.message);
    const readFriendlyErrorMessage =
      getReadFriendlyErrorMessage(e.message) ??
      "There was an error retrieving the user answer.";
    defaultErrorHandler(readFriendlyErrorMessage);
  }
}

/**
 * Marks any feedback sent to the logged in user as read for
 * a specific question on a specific task attempt.
 *
 * Note that the API method will only consider feedback within
 * the same fixed time interval that is used to actually query
 * the feedback.
 *
 * @param {number} attemptId
 * @param {number} questionNumber
 * @returns {Promise<void>}
 */
export async function markFeedbackAsRead(attemptId, questionNumber) {
  try {
    const response = await fetch("/api/tasks/feedback/markread", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ aaid: attemptId, qnum: questionNumber }),
    });
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.message);
    }
  } catch (e) {
    console.log(e.message);
    const readFriendlyErrorMessage =
      getReadFriendlyErrorMessage(e.message) ??
      "There was an error marking feedback as read.";
    defaultErrorHandler(readFriendlyErrorMessage);
  }
}

/**
 * Marks all feedback sent to the logged in user as read.
 *
 * Note that the API method will only consider feedback within
 * the same fixed time interval that is used to actually query
 * the feedback.
 *
 * @returns {Promise<void>}
 */
export async function markAllFeedbackAsRead() {
  try {
    const response = await fetch("/api/tasks/feedback/markallread", {
      method: "POST",
    });
    if (!response.ok) {
      const e = await response.json();
      throw new Error(e.message);
    }
  } catch (e) {
    console.log(e.message);
    const readFriendlyErrorMessage =
      getReadFriendlyErrorMessage(e.message) ??
      "There was an error marking all feedback as read.";
    defaultErrorHandler(readFriendlyErrorMessage);
  }
}
